import { useEffect, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import ProductRow from './ProductRow';

// "מבצעים": מוצרים במבצע "מחיר מיוחד", לפי אחוז ההנחה (spGetDeals). פתוח לכולם.
// סינון לפי חנות וחיפוש בתוך המבצעים; 50 בכל פעם ("טען עוד"). נטען בפעם הראשונה שהטאב נפתח (active).
const SEARCH_DEBOUNCE_MS = 400;

const s = {
  intro:    { margin: '0 0 14px', color: '#59636e', fontSize: 14 },
  tools:    { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  chip:     { padding: '7px 14px', fontSize: 14, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 20, background: '#fff', color: '#1f2328' },
  chipOn:   { borderColor: '#1a3f9c', background: '#1a3f9c', color: '#fff' },
  chipCount:{ fontWeight: 400, opacity: 0.8, marginInlineStart: 4 },
  search:   { flex: '1 1 220px', padding: '9px 12px', fontSize: 15, border: '1px solid #d0d7de', borderRadius: 10 },
  count:    { fontSize: 13, color: '#59636e', marginBottom: 10 },
  list:     { display: 'flex', flexDirection: 'column', gap: 10 },
  message:  { textAlign: 'center', color: '#59636e', padding: '40px 16px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  error:    { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 12 },
  more:     { alignSelf: 'center', margin: '6px 0 10px', padding: '10px 22px', border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c', fontWeight: 600 },
};

const DealsTab = ({ active }) => {
  const [storeId, setStoreId] = useState(null);
  const [q, setQ] = useState('');
  const [stores, setStores] = useState([]);
  const [storeDeals, setStoreDeals] = useState({});   // storeId → כמה מבצעים
  const [items, setItems] = useState(null);           // null = עוד לא נטען
  const [pricesByItem, setPricesByItem] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const opened = useRef(false);

  // append=false: עמוד ראשון (פתיחה / סינון / חיפוש); true: "טען עוד"
  const load = async (store, query, pageNum, append) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await API.getDeals(store, query.trim(), pageNum);
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code !== 0) { setError(status?.msg || 'שגיאה בטעינת המבצעים'); return; }
      const byItem = {};
      (resp[3] || []).forEach((p) => {
        if (!byItem[p.itemId]) byItem[p.itemId] = {};
        byItem[p.itemId][p.storeId] = p;
      });
      const newItems = resp[2] || [];
      const counts = {};
      (resp[4] || []).forEach((x) => { counts[x.storeId] = x.dealCount; });
      setStores(resp[1] || []);
      setStoreDeals(counts);
      setItems((prev) => (append && prev ? [...prev, ...newItems] : newItems));
      setPricesByItem((prev) => (append ? { ...prev, ...byItem } : byItem));
      setTotalCount(newItems[0]?.totalCount ?? (append ? totalCount : 0));
      setPage(pageNum);
    } catch (err) {
      if (id === requestId.current) setError(err.message || 'שגיאה בטעינת המבצעים');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // פתיחה ראשונה של הטאב
  useEffect(() => {
    if (active && !opened.current) { opened.current = true; load(storeId, q, 0, false); }
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // חיפוש (אחרי הפסקה קצרה בהקלדה)
  const firstQ = useRef(true);
  useEffect(() => {
    if (firstQ.current) { firstQ.current = false; return undefined; }
    const timer = setTimeout(() => load(storeId, q, 0, false), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickStore = (id) => {
    setStoreId(id);
    load(id, q, 0, false);
  };

  const storeName = (id) => stores.find((st) => st.id === id)?.name || '';

  return (
    <div>
      <p style={s.intro}>מוצרים במבצע "מחיר מיוחד" באתרי החנויות, לפי גובה ההנחה. המחיר במבצע מוצג בגדול, והמחיר הרגיל מחוק.</p>

      <div style={s.tools}>
        <button type="button" style={{ ...s.chip, ...(storeId === null ? s.chipOn : null) }} onClick={() => pickStore(null)}>
          כל החנויות
        </button>
        {stores.map((st) => (
          <button key={st.id} type="button" style={{ ...s.chip, ...(storeId === st.id ? s.chipOn : null) }} onClick={() => pickStore(st.id)}>
            {st.name}{storeDeals[st.id] ? <span style={s.chipCount}>({storeDeals[st.id]})</span> : null}
          </button>
        ))}
        <input style={s.search} type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="חיפוש במבצעים" aria-label="חיפוש במבצעים" />
      </div>

      {error && <div style={s.error} role="alert">{error}</div>}

      {items === null ? (
        <div style={s.message}>טוען מבצעים…</div>
      ) : (
        <>
          <div style={s.count}>
            {loading && !items.length ? 'טוען…' : `${totalCount.toLocaleString('he-IL')} מוצרים במבצע`}
            {storeId ? ` ב${storeName(storeId)}` : ''}{q.trim() ? ` עבור "${q.trim()}"` : ''}
          </div>
          {!loading && items.length === 0 && (
            <div style={s.message}>{q.trim() ? `אין מבצעים עבור "${q.trim()}"` : 'אין כרגע מבצעים'}</div>
          )}
          <div style={s.list}>
            {items.map((item) => (
              <ProductRow key={item.id} item={item} stores={stores} prices={pricesByItem[item.id] || {}}
                          badge={{ text: `-${Math.round(Number(item.discountPct))}%`, tone: 'deal' }} />
            ))}
            {items.length < totalCount && (
              <button type="button" style={s.more} onClick={() => load(storeId, q, page + 1, true)} disabled={loading}>
                {loading ? 'טוען…' : `טען עוד (${(totalCount - items.length).toLocaleString('he-IL')} נוספים)`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DealsTab;
